import { formatCurrency } from '../../utils/format';
import type { CouponData } from '../../types';
import type { Checkout } from '../../types/checkout';

interface ProductCardProps {
  data: Checkout;
  coupon?: CouponData;
  installments: {
    installment: number;
    total: number;
    perInstallment: number;
  }[];
  selectedPaymentMethod?: 'CREDIT_CARD' | 'PIX' | 'BOLETO';
}

export function ProductCard({
  data,
  coupon,
  installments,
  selectedPaymentMethod,
}: ProductCardProps) {
  const hasDiscount = data.amount && data.amount < (data.max_amount || 0);

  // Aplicar desconto do cupom
  const couponDiscount = coupon?.isApplied ? coupon.discount : 0;

  // Aplicar desconto PIX se método selecionado for PIX
  const pixDiscount =
    selectedPaymentMethod === 'PIX' ? data.pix_discount_percentage || 0 : 0;

  // Calcular preço final com todos os descontos
  let finalPrice = data.amount || 0;

  // Aplicar desconto do cupom
  if (couponDiscount > 0) {
    finalPrice = finalPrice * (1 - couponDiscount / 100);
  }

  // Aplicar desconto PIX
  if (pixDiscount > 0) {
    finalPrice = finalPrice * (1 - pixDiscount / 100);
  }

  const totalSavings =
    hasDiscount || couponDiscount > 0 || pixDiscount > 0
      ? (data.amount || 0) - finalPrice
      : 0;

  const squareImage = data.attachments?.find(
    attachment => attachment.attachment_type === 'square'
  )?.url;

  return (
    <div className="group overflow-hidden rounded-xl border border-white/50 bg-white shadow-md backdrop-blur-sm transition-all duration-500">
      <div className="p-6">
        {/* Card superior - Imagem quadrada + Nome do evento */}
        <div className="mb-6 flex items-start space-x-4">
          {/* Imagem quadrada */}
          <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg">
            <img
              src={squareImage}
              alt={data.name}
              className="h-full w-full object-cover transition-all duration-700 group-hover:scale-110"
              onError={e => {
                const target = e.target as HTMLImageElement;
                target.src =
                  'data:image/svg+xml,' +
                  encodeURIComponent(`
                  <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#8b5cf6;stop-opacity:1" />
                      </linearGradient>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grad)" rx="8"/>
                    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-family="system-ui" font-size="12" font-weight="bold">
                      ${data.name.substring(0, 2)}
                    </text>
                  </svg>
                `);
              }}
            />
            {hasDiscount && (
              <div className="absolute -right-1 -top-1 animate-pulse-soft rounded-full bg-gradient-to-r from-red-500 to-red-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-lg">
                -{data.amount || 0}%
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </div>

          {/* Nome do evento */}
          <div className="flex-1">
            <h3 className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-lg font-semibold text-transparent transition-all duration-500 group-hover:from-blue-600 group-hover:to-purple-600">
              {data.name}
            </h3>
            <div className="mt-1 flex items-center text-xs text-gray-500">
              <span>{data.description}</span>
            </div>
          </div>
        </div>

        {/* Descrição do evento */}
        {/* <div className="mb-6 border-b border-gray-100 pb-6">
          <p className="text-sm leading-relaxed text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
            {data.description}
          </p>
        </div> */}

        {/* Totalizador */}
        <div className="rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-semibold uppercase tracking-wide text-gray-600">
                TOTAL
              </span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">
                {installments.length > 1 ? `${installments.length}x de ` : null}
                {formatCurrency(
                  installments[installments.length - 1].perInstallment
                )}
              </div>
              {installments.length > 1 ? (
                <div className="mt-1 text-base text-gray-500">
                  ou {formatCurrency(data.amount || 0)} à vista
                </div>
              ) : null}
            </div>
          </div>

          {/* Detalhes dos descontos */}
          {(hasDiscount || couponDiscount > 0 || pixDiscount > 0) && (
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Preço original:</span>
                <span>{formatCurrency(data.amount || 0)}</span>
              </div>
              {hasDiscount && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto promocional ({data.amount || 0}%):</span>
                  <span>
                    -{formatCurrency((data.amount || 0) - finalPrice)}
                  </span>
                </div>
              )}
              {coupon?.isApplied && (
                <div className="flex justify-between text-sm text-blue-600">
                  <span>
                    Cupom {coupon.code} ({coupon.discount}%):
                  </span>
                  <span>-{formatCurrency(finalPrice - finalPrice)}</span>
                </div>
              )}
              {pixDiscount > 0 && (
                <div className="flex justify-between text-sm text-blue-600">
                  <span>Desconto PIX ({pixDiscount}%):</span>
                  <span>
                    -{formatCurrency((data.amount || 0) * (pixDiscount / 100))}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-semibold text-green-600">
                <span>Total economizado:</span>
                <span>{formatCurrency(totalSavings)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
                <span className="text-lg">Valor Final:</span>
                <span>{formatCurrency(finalPrice)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Garantia */}
        <div className="mt-4 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-3">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700">
              Garantia de 7 dias
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            100% do seu dinheiro de volta se não ficar satisfeito
          </p>
        </div>
      </div>
    </div>
  );
}
